import React, { useState, useRef, useEffect } from 'react';
import Window from '../os/Window';

const CHAT_API_BASE = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:3002';
const MAX_HISTORY = 20;

type LineKind = 'system' | 'user' | 'assistant' | 'error';

interface TerminalLine {
    kind: LineKind;
    text: string;
}

const WELCOME_LINES: TerminalLine[] = [
    { kind: 'system', text: 'Chat about Filips - type a message and press Enter.' },
    { kind: 'system', text: '' },
];

const TerminalApp: React.FC<WindowAppProps> = (props) => {
    const [lines, setLines] = useState<TerminalLine[]>(WELCOME_LINES);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
    }, [lines, loading]);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        setInput('');
        setLines((prev) => [...prev, { kind: 'user', text: `$ ${text}` }, { kind: 'assistant', text: '...' }]);
        setLoading(true);

        const newMessages = [...messages, { role: 'user' as const, content: text }];

        try {
            const res = await fetch(`${CHAT_API_BASE}/api/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: newMessages.slice(-MAX_HISTORY),
                }),
            });

            const data = await res.json().catch(() => ({}));

            setLines((prev) => {
                const out = prev.slice(0, -1);
                if (!res.ok) {
                    out.push({
                        kind: 'error',
                        text: data?.error || `Error ${res.status}. Is the chat server running on port 3002?`,
                    });
                    return out;
                }
                let content = typeof data?.content === 'string' ? data.content.trim() : '';
                content = content.replace(/\*\*([^*]*)\*\*/g, '$1').replace(/\*/g, '').replace(/\|/g, ' ');
                const replyLines = content ? content.split('\n').map((l: string) => l.trim()).filter(Boolean) : ['(No response.)'];
                replyLines.forEach((l: string) => out.push({ kind: 'assistant', text: l }));
                return out;
            });

            if (res.ok && typeof data?.content === 'string') {
                setMessages((prev) => [...prev.slice(-(MAX_HISTORY - 1)), { role: 'user', content: text }, { role: 'assistant', content: data.content }]);
            }
        } catch (_) {
            setLines((prev) => {
                const out = prev.slice(0, -1);
                out.push({ kind: 'error', text: 'Could not reach chat server. In another terminal run: cd portfolio-inner-site && npm run server' });
                return out;
            });
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <Window
            top={80}
            left={80}
            width={720}
            height={500}
            windowTitle="Terminal"
            windowBarIcon="terminalIcon"
            closeWindow={props.onClose}
            onInteract={props.onInteract}
            minimizeWindow={props.onMinimize}
        >
            <div style={styles.terminal}>
                <div style={styles.scrollArea} ref={scrollRef}>
                    <div style={styles.contentColumn}>
                        {lines.map((line, i) => (
                            <div
                                key={i}
                                style={{
                                    ...styles.line,
                                    ...(line.kind === 'user' ? styles.lineUser : {}),
                                    ...(line.kind === 'assistant' ? styles.lineAssistant : {}),
                                    ...(line.kind === 'error' ? styles.lineError : {}),
                                }}
                            >
                                {line.text}
                            </div>
                        ))}
                        {loading && (
                            <div style={styles.line}>
                                <span style={styles.cursor}>_</span>
                            </div>
                        )}
                        <div style={styles.promptRow}>
                            <span style={styles.promptPrefix}>$ </span>
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={loading}
                                placeholder=""
                                style={styles.input}
                                aria-label="Chat message"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Window>
    );
};

const styles: StyleSheetCSS = {
    terminal: {
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 12,
        backgroundColor: '#1e1e1e',
        fontFamily: 'ui-monospace, "Cascadia Code", "SF Mono", Monaco, "Courier New", monospace',
        fontSize: 13,
        color: '#d4d4d4',
        overflow: 'hidden',
        lineHeight: 1.5,
        minWidth: 0,
    },
    scrollArea: {
        position: 'relative',
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        minWidth: 0,
    },
    contentColumn: {
        display: 'block',
        width: '100%',
        minWidth: 0,
    },
    line: {
        display: 'block',
        marginBottom: 2,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        width: '100%',
    },
    lineUser: {
        color: '#4ec9b0',
    },
    lineAssistant: {
        color: '#d4d4d4',
    },
    lineError: {
        color: '#f48771',
    },
    promptRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        marginTop: 2,
        minWidth: 0,
    },
    promptPrefix: {
        color: '#4ec9b0',
        flexShrink: 0,
    },
    input: {
        flex: 1,
        minWidth: 0,
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        color: '#d4d4d4',
        fontFamily: 'inherit',
        fontSize: 'inherit',
    },
    cursor: {
        animation: 'blink 1s step-end infinite',
    },
};

export default TerminalApp;
